/* eslint-disable no-console */
const { getElements, parseInteger } = window.BAO.Utils
class SectionAsync extends window.BAO.CustomElement() {
  static get observedAttributes () {
    return ['section-id', 'url', 'loading', 'cache-key', 'cache-expiry']
  }

  static get requiredAttributes () {
    return ['section-id']
  }

  /**
   * Loads a section asynchronously
   */
  connectedCallback () {
    super.connectedCallback()

    if (!this.useCache && this.isLazy && this.isIntersectionObserverSupported) {
      this.intersectionObserverCallback = this.intersectionObserverCallback.bind(
        this,
      )
      this.observer.observe(this)
    } else {
      this.renderSection()
    }
  }

  /**
   * Renders the custom element
   */
  async renderSection () {
    this.isBusy = true
    const cachedHtml = this.cachedHtml
    const html =
      cachedHtml ||
      (await this.sectionRenderer.fetchSingle(this.sectionId, this.sectionUrl))
    this.sectionHTML = this.sectionRenderer.getInnerHtml(html)
    this.isBusy = false

    if (this.useCache && !cachedHtml) {
      this.storeHtmlInCache(html)
    }

    if (!this.url?.includes('nosto=')) return

    const parser = new DOMParser()
    const doc = parser.parseFromString(this.sectionHTML, 'text/html')
    const anchors = doc.querySelectorAll('a')

    anchors.forEach(anchor => {
      anchor.setAttribute('href', this.url)
    })

    // Update the sectionHTML with the modified HTML
    this.sectionHTML = doc.body.innerHTML
    const buyNowButton = this.querySelector('[data-product-add-to-cart]')
    if (!buyNowButton) return

    buyNowButton.addEventListener('click', () => {
      const nostoProductId = buyNowButton.closest('section-async').dataset
        .productId
      const nostoElementId = buyNowButton.closest('.nosto_element').id

      // eslint-disable-next-line no-undef
      nostojs(api => {
        api.recommendedProductAddedToCart(nostoProductId, nostoElementId)
      })
    })
  }

  /**
   * Removes the parent element
   */
  removeParentElement () {
    const elementToRemove = this.parentIdentifier
      ? this.closest(this.parentIdentifier)
      : this
    elementToRemove.remove()
  }

  /**
   * Calls the initialise function when the custom element is intersecting (visible in the viewport)
   */
  intersectionObserverCallback (entries) {
    const entry = entries?.[0]
    if (!entry?.isIntersecting) return
    this.renderSection()
    this.observer.disconnect()
  }

  /**
   * @returns {IntersectionObserver} an observer that specifies when an element is intersecting (visible in viewport)
   */
  get observer () {
    // eslint-disable-next-line compat/compat
    return new IntersectionObserver(this.intersectionObserverCallback)
  }

  /**
   * @returns {string} The ID of the section to be rendered
   */
  get sectionId () {
    return this.getAttribute('section-id')
  }

  /**
   * @param {string} html - HTML to be rendered
   */
  set sectionHTML (html) {
    if (!html) {
      /*
       * When there isn't any HTML we want to remove the parent element
       * to ensure we're not showing other elements of the section
       * such as kicker and title
       */
      this.removeParentElement()
      return
    }
    this.innerHTML = html
    this.enableDependantElements()
  }

  enableDependantElements () {
    getElements(`[data-await='${this.sectionId}']`).forEach(
      el => (el.disabled = false),
    )
  }

  storeHtmlInCache (html) {
    const now = new Date()
    const item = {
      value: html,
      expiry: now.getTime() + this.cacheExpiryInMilliseconds,
    }
    localStorage.setItem(this.cacheKey, JSON.stringify(item))
  }

  /**
   * @returns {string} html - HTML of the section
   */
  get sectionHTML () {
    return this.innerHTML
  }

  /**
   * @param {boolean} isBusy - Whether or not the element is currently busy
   */
  set isBusy (isBusy) {
    this.setAttribute('aria-busy', String(isBusy))
  }

  /**
   * @returns {boolean} Whether or not the element is currently busy
   */
  get isBusy () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /**
   * @returns {sectionRenderer} The BAO sectionRenderer class
   */
  get sectionRenderer () {
    return window.BAO.sectionRenderer
  }

  /**
   * @returns {string} The URL from which to request the section
   */
  get url () {
    return this.getAttribute('url')?.replace(
      '[[query_parameters]]',
      window.location.search,
    )
  }

  /**
   * @returns {string} The identifier of a parent element (if provided)
   */
  get parentIdentifier () {
    return this?.dataset?.parentIdentifier
  }

  /**
   * @returns {string} The url, pathname and query parameters
   */
  get sectionUrl () {
    let returnUrl = this.url
    const rootPath = window.Shopify.routes.root.slice(0, -1)

    if (this.url.startsWith('http://') || this.url.startsWith('https://')) {
      const urlCheck = new URL(this.url)
      if (!urlCheck.pathname.startsWith(rootPath)) {
        const replacing = `${urlCheck.hostname}`
        const replacingWith = `${replacing}${rootPath}`
        returnUrl = returnUrl.replace(replacing, replacingWith)
      }
    } else {
      if (!this.url.startsWith(rootPath)) {
        returnUrl = `${rootPath}${returnUrl}`
      }
    }

    return returnUrl || `${window.location.pathname}${window.location.search}`
  }

  /**
   * @returns {boolean} Whether or not this section should load when it's needed instead of at pageload
   */
  get isLazy () {
    return this.getAttribute('loading') === 'lazy'
  }

  /**
   * @returns {boolean} Whether or not this section should be cached
   */
  get useCache () {
    return this.hasAttribute('cache-key')
  }

  /**
   * @returns {string} The key to use to cache this section against
   */
  get cacheKey () {
    return this.getAttribute('cache-key')
  }

  /**
   * @returns {number} The length of time this section should be cached for, in hours
   */
  get cacheExpiry () {
    return parseInteger(this.getAttribute('cache-expiry'))
  }

  /**
   * @returns {number} The length of time this section should be cached for, in milliseconds
   */
  get cacheExpiryInMilliseconds () {
    return this.cacheExpiry * 3600000
  }

  /**
   * @returns {string} The cached html for the requested section
   */
  get cachedHtml () {
    const itemString = localStorage.getItem(this.cacheKey)
    if (!itemString) {
      return null
    }
    const item = JSON.parse(itemString)
    const now = new Date()
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(this.cacheKey)
      return null
    }
    return item.value
  }

  /**
   * @returns {boolean} Whether or not IntersectionObserver is supported by the browser
   */
  get isIntersectionObserverSupported () {
    return `IntersectionObserver` in window
  }
}

if (!customElements.get('section-async')) {
  customElements.define('section-async', SectionAsync)
}
